"use client";

import { c as _c } from "react/compiler-runtime";
import { cva } from "class-variance-authority";
import { Collapsible, CollapsibleContent } from "fumadocs-ui/components/ui/collapsible";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { usePathname } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { type SyntheticEvent, useEffect, useReducer, useState } from "react";
import { cn } from "../lib/cn";
import { buttonVariants } from "./ui/button";
const rateButtonVariants = cva("inline-flex items-center gap-2 px-3 py-2 rounded-full font-medium border text-sm [&_svg]:size-4 disabled:cursor-not-allowed", {
  variants: {
    active: {
      true: "bg-fd-accent text-fd-accent-foreground [&_svg]:fill-current",
      false: "text-fd-muted-foreground"
    }
  }
});
export interface Feedback {
  opinion: "good" | "bad";
  url?: string;
  message: string;
}

// localStorage throws outright in Safari Private Browsing and when site data is
// blocked, so persistence is best-effort: it must never take the widget down or
// block the confirmation UI.
function readStoredFeedback(url: string): Feedback | null {
  try {
    const item = localStorage.getItem(`docs-feedback-${url}`);
    return item === null ? null : JSON.parse(item) as Feedback;
  } catch {
    return null;
  }
}
function writeStoredFeedback(url: string, result: Feedback | null) {
  try {
    if (result) {
      localStorage.setItem(`docs-feedback-${url}`, JSON.stringify(result));
    } else {
      localStorage.removeItem(`docs-feedback-${url}`);
    }
  } catch {
    // Ignore: the rating is already recorded server-side and in PostHog.
  }
}
export function Feedback(t0) {
  const $ = _c(37);
  const {
    githubUrl
  } = t0;
  const url = usePathname();
  const posthog = usePostHog();
  const [previous, replacePrevious] = useReducer(_temp, null);
  const [opinion, setOpinion] = useState(null);
  const [message, setMessage] = useState("");
  let t1;
  let t2;
  if ($[0] !== url) {
    t1 = () => {
      replacePrevious(readStoredFeedback(url));
    };
    t2 = [url];
    $[0] = url;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== message || $[4] !== opinion || $[5] !== posthog || $[6] !== url) {
    t3 = function submit(e) {
      if (opinion === null) {
        return;
      }
      const feedback = {
        opinion,
        message
      };
      posthog.capture("on_rate_docs", {
        ...feedback,
        url
      });
      replacePrevious(feedback);
      setMessage("");
      setOpinion(null);
      writeStoredFeedback(url, feedback);
      e?.preventDefault();
    };
    $[3] = message;
    $[4] = opinion;
    $[5] = posthog;
    $[6] = url;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const submit = t3;
  const activeOpinion = previous?.opinion ?? opinion;
  const t4 = opinion !== null || previous !== null;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = v => {
      if (!v) {
        setOpinion(null);
      }
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <p className="text-sm font-medium pe-2">How is this guide?</p>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = previous !== null;
  let t8;
  if ($[10] !== activeOpinion) {
    t8 = cn(rateButtonVariants({
      active: activeOpinion === "good"
    }));
    $[10] = activeOpinion;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t10;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setOpinion("good");
    };
    t10 = <ThumbsUp />;
    $[12] = t10;
    $[13] = t9;
  } else {
    t10 = $[12];
    t9 = $[13];
  }
  let t11;
  if ($[14] !== t7 || $[15] !== t8) {
    t11 = <button disabled={t7} className={t8} onClick={t9}>{t10}Good</button>;
    $[14] = t7;
    $[15] = t8;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = previous !== null;
  let t13;
  if ($[17] !== activeOpinion) {
    t13 = cn(rateButtonVariants({
      active: activeOpinion === "bad"
    }));
    $[17] = activeOpinion;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  let t15;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      setOpinion("bad");
    };
    t15 = <ThumbsDown />;
    $[19] = t14;
    $[20] = t15;
  } else {
    t14 = $[19];
    t15 = $[20];
  }
  let t16;
  if ($[21] !== t12 || $[22] !== t13) {
    t16 = <button disabled={t12} className={t13} onClick={t14}>{t15}Bad</button>;
    $[21] = t12;
    $[22] = t13;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== t11 || $[25] !== t16) {
    t17 = <div className="flex flex-row items-center gap-2">{t6}{t11}{t16}</div>;
    $[24] = t11;
    $[25] = t16;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== githubUrl || $[28] !== message || $[29] !== previous || $[30] !== submit || $[31] !== url) {
    t18 = <CollapsibleContent className="mt-3">{previous ? <div className="px-3 py-6 flex flex-col items-center gap-3 bg-fd-card text-fd-muted-foreground text-sm text-center rounded-xl"><p>Thank you for your feedback!</p><div className="flex flex-row items-center gap-2"><a href={githubUrl} rel="noreferrer noopener" target="_blank" className={cn(buttonVariants({
            color: "primary"
          }), "text-xs")}>View on GitHub</a><button className={cn(buttonVariants({
            color: "secondary"
          }), "text-xs")} onClick={() => {
            setOpinion(previous.opinion);
            replacePrevious(null);
            writeStoredFeedback(url, null);
          }}>Submit Again</button></div></div> : <form className="flex flex-col gap-3" onSubmit={submit}><textarea autoFocus={true} required={true} value={message} onChange={e_0 => {
          setMessage(e_0.target.value);
        }} className="border rounded-lg bg-fd-secondary text-fd-secondary-foreground p-3 resize-none focus-visible:outline-none placeholder:text-fd-muted-foreground" placeholder="Leave your feedback..." onKeyDown={_temp2} /><button type="submit" className={cn(buttonVariants({
          color: "outline"
        }), "w-fit px-3")}>Submit</button></form>}</CollapsibleContent>;
    $[27] = githubUrl;
    $[28] = message;
    $[29] = previous;
    $[30] = submit;
    $[31] = url;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t17 || $[34] !== t18 || $[35] !== t4) {
    t19 = <Collapsible open={t4} onOpenChange={t5} className="border-y py-3">{t17}{t18}</Collapsible>;
    $[33] = t17;
    $[34] = t18;
    $[35] = t4;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  return t19;
}
function _temp2(e_1) {
  if (!e_1.shiftKey && e_1.key === "Enter") {
    e_1.preventDefault();
    e_1.currentTarget.form?.requestSubmit();
  }
}
function _temp(_previous, nextPrevious) {
  return nextPrevious;
}